import { useEffect, useRef, useState } from "react";
import { Send, MessageCircle, Phone, ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

/**
 * WhatsAppSimulator
 * --------------------------------------------------------------------------
 * A web-based stand-in for the WhatsApp chat surface. Pipes user text to
 * /api/whatsapp/inbound (the same endpoint the Meta Cloud API webhook will
 * hit once Business API approval comes through). Lets the user test the bot
 * commands today: HELP, CASH, DEBT, STOCK, WEEK, MONTH, REPORT, LOAN, PRO,
 * 1P / 1R, RESTOCK 3 BY 5, plus natural language transactions.
 */
export default function WhatsAppSimulator() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([
    { id: "welcome", role: "bot", text: "👋 Hello! Type *HELP* to see what I can do, or just chat naturally — \"I sold 3 bags of rice for 45000\"." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || !user?.phone) return;
    const mid = `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setMessages((m) => [...m, { id: mid, role: "me", text }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/whatsapp/inbound", { from_phone: user.phone, text });
      const data = r.data;
      const out = { id: `${mid}_r`, role: "bot", text: data.reply };
      if (data.image_url) {
        const proxied = data.image_url.startsWith("/api") ? data.image_url : `/api${data.image_url}`;
        out.imageEndpoint = proxied;
      }
      setMessages((m) => [...m, out]);
    } catch (e) {
      setMessages((m) => [...m, { id: `${mid}_err`, role: "bot", text: "⚠️ Couldn't reach the bot. Try again." }]);
    } finally { setBusy(false); }
  };

  const QUICK = ["HELP", "CASH", "DEBT", "STOCK", "REPORT", "WEEK", "MONTH", "PRO"];

  return (
    <div className="px-4 py-5 pb-6">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center">
          <MessageCircle className="w-4 h-4" />
        </div>
        <div>
          <div className="font-heading text-base font-bold leading-tight">WhatsApp Preview</div>
          <div className="text-[11px] text-kudi-ink/55 flex items-center gap-1"><Phone className="w-3 h-3" /> {user?.phone || "—"}</div>
        </div>
      </div>

      {/* Chat surface */}
      <div ref={scrollRef} className="bg-[#ECE5DD] rounded-2xl p-3 h-[60dvh] overflow-y-auto space-y-2 border border-border" data-testid="wa-sim-chat">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "me" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words shadow-sm ${
              m.role === "me" ? "bg-[#DCF8C6] text-kudi-ink rounded-br-sm" : "bg-white text-kudi-ink rounded-bl-sm"
            }`}>
              {m.text}
              {m.imageEndpoint && (
                <ReportImage endpoint={m.imageEndpoint} />
              )}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-white rounded-2xl px-3 py-2 text-sm rounded-bl-sm shadow-sm text-kudi-ink/40">typing...</div></div>}
      </div>

      {/* Quick chips */}
      <div className="flex gap-2 mt-3 overflow-x-auto pb-1 -mx-1 px-1" data-testid="wa-sim-quick">
        {QUICK.map((q) => (
          <button key={q} onClick={() => send(q)} disabled={busy} className="shrink-0 bg-white border border-border rounded-full px-3 py-1.5 text-xs font-semibold text-kudi-green hover:bg-accent transition disabled:opacity-50" data-testid={`wa-quick-${q.toLowerCase()}`}>
            {q}
          </button>
        ))}
      </div>

      {/* Input */}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2 items-end">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type like in WhatsApp..."
          className="flex-1 bg-white border border-border rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          data-testid="wa-sim-input"
        />
        <button type="submit" disabled={busy || !input.trim()} className="w-11 h-11 rounded-full bg-emerald-600 text-white flex items-center justify-center disabled:opacity-50 active:scale-95 transition" data-testid="wa-sim-send">
          <Send className="w-4 h-4" />
        </button>
      </form>

      <p className="text-[11px] text-kudi-ink/50 mt-3 text-center">
        This previews the exact bot you'll type to inside WhatsApp once Meta approval comes through. Same endpoint, same behaviour.
      </p>
    </div>
  );
}

/**
 * Lazy authed loader for an /api PNG (so we can show inline previews in chat).
 * We fetch with the user's JWT, then convert blob → object URL.
 */
function ReportImage({ endpoint }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        // axios via api.js handles auth header
        const r = await api.get(endpoint, { responseType: "blob" });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(r.data);
        setSrc(objectUrl);
      } catch {
        setErr(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [endpoint]);

  if (err) return <div className="mt-2 text-xs text-rose-600 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Image failed to load</div>;
  if (!src) return <div className="mt-2 text-xs text-kudi-ink/40">Loading image...</div>;
  return <img src={src} alt="Kobolog report" className="mt-2 rounded-xl w-full" data-testid="wa-sim-image" />;
}
